import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ClipboardCheck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import PageHeader from "@/components/PageHeader";
import GroupCheckCard from "@/components/GroupCheckCard";
import api, { errMsg } from "@/lib/api";

const byOldestCheck = (a, b) => (a.checked_at ? new Date(a.checked_at).getTime() : 0) - (b.checked_at ? new Date(b.checked_at).getTime() : 0);

function Summary({ s }) {
  return (
    <div className="panel fade-up p-10 text-center" data-testid="group-check-finished">
      <ClipboardCheck className="mx-auto h-10 w-10 text-emerald-400" />
      <h2 className="mt-4 text-2xl font-bold">Semakan selesai</h2>
      <p className="mt-2 text-sm text-muted-foreground" data-testid="group-check-summary">
        {s.active} aktif · {s.inactive} tidak aktif · {s.deleted} dipadam · {s.skipped} dilangkau
      </p>
      <div className="mt-6 flex justify-center gap-2">
        <Button asChild><Link to="/kumpulan">Kembali ke Kumpulan</Link></Button>
        {s.inactive > 0 && <Button asChild variant="secondary"><Link to="/kumpulan?status=inactive" data-testid="group-check-view-inactive">Lihat group tidak aktif</Link></Button>}
      </div>
    </div>
  );
}

export default function GroupCheck() {
  const [groups, setGroups] = useState(null);
  const [index, setIndex] = useState(0);
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState({ active: 0, inactive: 0, deleted: 0, skipped: 0 });

  useEffect(() => { api.get("/groups").then((r) => setGroups([...r.data].sort(byOldestCheck))).catch(() => setGroups([])); }, []);

  const next = (key) => { setS((p) => ({ ...p, [key]: p[key] + 1 })); setIndex((i) => i + 1); setOpened(false); };

  const run = async (fn, key) => {
    setBusy(true);
    try { await fn(); next(key); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const g = groups?.[index];
  const check = (status) => run(() => api.post(`/groups/${g.id}/check`, { status }), status);
  const remove = () => window.confirm(`Padam "${g.name}" dari senarai?`) && run(() => api.delete(`/groups/${g.id}`), "deleted");
  const total = groups?.length || 0;

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader eyebrow="Semak Group" title="Semak senarai group" desc="Buka setiap group untuk pastikan ia masih wujud dan anda masih ahli. Group yang paling lama tidak disemak dipaparkan dahulu."
        action={<Button asChild variant="ghost" data-testid="group-check-back-btn"><Link to="/kumpulan"><ArrowLeft className="mr-2 h-4 w-4" /> Kumpulan</Link></Button>} />
      {groups === null ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : total === 0 ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground" data-testid="group-check-empty">Tiada group untuk disemak. <Link to="/kumpulan" className="text-primary hover:underline">Tambah group</Link></div>
      ) : (
        <>
          <div className="mb-6" data-testid="group-check-progress">
            <div className="mb-2 flex justify-between font-mono text-xs text-muted-foreground">
              <span>Group {Math.min(index + 1, total)} / {total}</span>
              <span>{s.active} aktif · {s.inactive + s.deleted} tidak aktif</span>
            </div>
            <Progress value={(index / total) * 100} className="h-2" />
          </div>
          {g ? (
            <GroupCheckCard key={g.id} group={g} opened={opened} setOpened={setOpened} busy={busy}
              onActive={() => check("active")} onInactive={() => check("inactive")} onDelete={remove} onSkip={() => next("skipped")} />
          ) : <Summary s={s} />}
        </>
      )}
    </div>
  );
}
